// src/utils/errorHandler.js

/**
 * Maps backend exception classes to user-friendly error messages
 */
export const getErrorMessage = (error) => {
  // Extract error data from response
  const errorData = error?.response?.data;
  const status = error?.response?.status;
  const message = errorData?.message || errorData?.error || error?.message;

  console.log('🔍 Error Details:', {
    status,
    message,
    errorData,
    fullError: error
  });

  // Backend exception mapping
  const exceptionPatterns = {
    // Coupon Validation Exceptions
    'CouponNotFoundException': 'Coupon code not found or has expired',
    'CouponInactiveException': 'This coupon is currently inactive',
    'CouponQuotaExceededException': 'This coupon has reached its usage limit',
    'CouponNotYetValidException': 'This coupon is not yet valid. Please check the start date',
    'CouponExpiredException': 'This coupon has expired',
    'CouponMinAmountException': message || 'Minimum order amount not met for this coupon',
    'CouponNotEligibleException': 'You are not eligible for this coupon',
    'CouponAlreadyUsedException': 'You have already used this coupon',
    'CouponValidationException': message || 'Coupon validation failed',

    // Booking Exceptions
    'ActiveBookingExistsException': 'Selected bike is already booked for the chosen dates/time',
    'BookingNotFoundException': 'Booking not found',
    'InvalidBookingException': 'Invalid booking details provided',

    // Authentication Exceptions
    'UnauthorizedException': 'Please login to continue',
    'ForbiddenException': 'You do not have permission to perform this action',

    // Validation Exceptions
    'ValidationException': message || 'Please check your input and try again',
    'InvalidInputException': message || 'Invalid input provided',
  };

  // Check if message contains exception class name
  if (message) {
    for (const [exceptionClass, userMessage] of Object.entries(exceptionPatterns)) {
      if (message.includes(exceptionClass) || errorData?.exception?.includes(exceptionClass)) {
        return userMessage;
      }
    }
  }

  // Status code-based messages
  switch (status) {
    case 400:
      return message || 'Invalid request. Please check your input';
    case 401:
      return 'Please login to continue';
    case 403:
      return 'You do not have permission to perform this action';
    case 404:
      return message || 'Requested resource not found';
    case 409:
      return message || 'This action conflicts with existing data';
    case 422:
      return message || 'Validation failed. Please check your input';
    case 500:
      return 'Server error. Please try again later or contact support';
    case 503:
      return 'Service temporarily unavailable. Please try again later';
    default:
      return message || 'Something went wrong. Please try again';
  }
};

/**
 * Get error type for styling (success, error, warning, info)
 */
export const getErrorType = (error) => {
  const status = error?.response?.status;
  
  if (status >= 500) return 'error'; // Server errors
  if (status === 404) return 'warning'; // Not found
  if (status === 401 || status === 403) return 'warning'; // Auth issues
  if (status >= 400) return 'error'; // Client errors
  
  return 'error'; // Default
};

/**
 * Format coupon-specific error messages with helpful context
 */
export const getCouponErrorDetails = (error) => {
  const message = getErrorMessage(error);
  const errorData = error?.response?.data;

  const details = {
    title: 'Coupon Not Applied',
    message: message,
    suggestions: []
  };

  // Add specific suggestions based on error type
  if (message.includes('expired')) {
    details.suggestions = [
      'Check if you have any active coupons',
      'Contact support for alternative offers'
    ];
  } else if (message.includes('minimum') || message.includes('Minimum')) {
    details.suggestions = [
      `Required minimum amount: ${errorData?.minimumAmount || 'Check coupon details'}`,
      'Add more items or select a different package'
    ];
  } else if (message.includes('already used')) {
    details.suggestions = [
      'This coupon can only be used once',
      'Check for other available coupons'
    ];
  } else if (message.includes('first booking')) {
    details.suggestions = [
      'This coupon is only valid for your first booking',
      'Complete this booking without coupon or use a different one'
    ];
  } else if (message.includes('not eligible')) {
    details.suggestions = [
      'This coupon may be for specific customers only',
      'Try a different coupon code'
    ];
  } else if (message.includes('not yet valid')) {
    details.suggestions = [
      'This coupon will be active soon',
      'Save it and use it when it becomes active'
    ];
  }

  return details;
};

/**
 * Format booking error messages
 */
export const getBookingErrorDetails = (error) => {
  const message = getErrorMessage(error);

  const details = {
    title: 'Booking Failed',
    message: message,
    suggestions: []
  };

  if (message.includes('already booked')) {
    details.suggestions = [
      'Try selecting different dates or times',
      'Choose a different vehicle',
      'Contact support for availability'
    ];
  } else if (message.includes('payment')) {
    details.suggestions = [
      'Check your payment method',
      'Try again or contact support if issue persists'
    ];
  }

  return details;
};
